/**
 * The listings map's preview-card popover — its GEOMETRY contract with Floating
 * UI, and its DOM contract with the browser.
 *
 * ── WHAT THIS REPLACES, AND WHY IT IS A LIBRARY CALL ────────────────────────
 *
 * The card used to be a second `AdvancedMarker` placed by hand:
 * `anchorLeft="50%"` / `anchorTop="100%"` plus a `translateY`, with above-vs-
 * below chosen from the map's LATITUDE midpoint (`map-popup-placement.ts`,
 * deleted with this change). Three things were wrong with that and only one of
 * them was the sign:
 *
 *   1. the anchor props are an OFFSET from the content's top-left corner, so
 *      `50%` meant "half a card-width to the RIGHT" — measured on production,
 *      the card's centre sat 320 px (one full card width) off the pin's;
 *   2. the flip decision was made from where the pin is in the WORLD, not from
 *      the space this card actually needs, so a card taller than the half it
 *      flipped into still did not fit;
 *   3. there was no horizontal shift at all, so a pin near a pane edge always
 *      produced a card clipped by the results sheet (measured: right edge 773
 *      against a pane that ends at 640).
 *
 * All three are the SAME mistake — computing popover geometry by hand — and the
 * remedy is to stop: a popover is a screen-space rectangle constrained by a
 * screen-space boundary, which is a solved problem with a standard answer.
 * `offset → flip → shift` against the map PANE is that answer, and it is
 * Floating UI's own default chain.
 *
 * ── ORDER IS SEMANTICS ──────────────────────────────────────────────────────
 *
 * Floating UI runs middleware as a PIPELINE: each one sees the coordinates the
 * previous one produced. `offset` must precede `flip` (flip's overflow
 * measurement has to include the gap, or a card that only just fits is placed
 * overlapping the pill), and `flip` must precede `shift` (flipping resets the
 * main axis, which would discard a shift computed before it). So the order is
 * not a style choice, and it is declared ONCE — {@link MAP_POPOVER_MIDDLEWARE}
 * — with {@link mapPopoverMiddleware} building the real chain by mapping over
 * that declaration. A unit test asserts the BUILT chain's `name`s, so the
 * declaration and the construction cannot drift.
 */
import { flip, offset, shift, type Middleware } from '@floating-ui/dom';

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/**
 * Above the pin, horizontally centred on it. `flip` turns this into `bottom`
 * when there is no room above — it is a PREFERENCE, not a promise.
 */
export const MAP_POPOVER_PLACEMENT = 'top' as const;

/** Clear space between the price pill's edge and the card's nearest edge, px. */
export const MAP_POPOVER_GAP_PX = 8;

/**
 * How close the card may come to the map pane's edge before `shift` slides it
 * back, px. The same value as the gap by intent, not by coincidence: both are
 * "the card never touches the thing it is next to".
 */
export const MAP_POPOVER_BOUNDARY_PADDING_PX = 8;

/**
 * The middleware chain, IN ORDER. See the module header for why each edge of
 * this ordering is load-bearing.
 */
export const MAP_POPOVER_MIDDLEWARE = ['offset', 'flip', 'shift'] as const;

/** One of {@link MAP_POPOVER_MIDDLEWARE}. */
export type MapPopoverMiddlewareName = (typeof MAP_POPOVER_MIDDLEWARE)[number];

/**
 * The chain, bound to the element that BOUNDS it.
 *
 * ⛔ The boundary is the map PANE, never the viewport. A popover clipped by the
 * results sheet is inside the window and outside the map, and only the second
 * of those is a fact about this card — which is the whole of defect (3) above.
 *
 * `boundary` is `null` before the pane mounts (one render). Floating UI's own
 * default, `'clippingAncestors'`, is the honest answer for that render: it is
 * not the pane, but it is not the window either, and the pane arrives on the
 * next one.
 */
export function mapPopoverMiddleware(boundary: Element | null): Middleware[] {
  const overflow = {
    boundary: boundary ?? ('clippingAncestors' as const),
    padding: MAP_POPOVER_BOUNDARY_PADDING_PX,
  };
  return MAP_POPOVER_MIDDLEWARE.map((name) => {
    switch (name) {
      case 'offset':
        return offset(MAP_POPOVER_GAP_PX);
      case 'flip':
        return flip(overflow);
      case 'shift':
        return shift(overflow);
    }
  });
}

/**
 * `absolute`, not `fixed`: the card is a CHILD of the pane, so the pane is its
 * offset parent and the coordinates come back pane-relative.
 */
export const MAP_POPOVER_STRATEGY = 'absolute' as const;

/**
 * THE GEOMETRY, as one value — everything `computePosition` needs except the
 * PLATFORM.
 *
 * ⛔ This exists so the component and its proof cannot compose different
 * geometries. `useFloating` is `computePosition` plus React plumbing plus the
 * DOM platform; the platform is the only part of it that needs a browser. So
 * the unit test beside this module substitutes the platform and NOTHING ELSE,
 * and runs the real chain over real rectangles — which is what lets the
 * anchor-sign defect this change fixes be caught in the PR lane rather than in
 * a nightly.
 *
 * A second spelling of `placement` / `strategy` / `middleware` at either call
 * site would make the test a test of a copy. There is one spelling, and it is
 * here.
 */
export function mapPopoverConfig(boundary: Element | null) {
  return {
    placement: MAP_POPOVER_PLACEMENT,
    strategy: MAP_POPOVER_STRATEGY,
    middleware: mapPopoverMiddleware(boundary),
  };
}

// ---------------------------------------------------------------------------
// DOM contract
// ---------------------------------------------------------------------------

/**
 * The price pill. Every marker publishes it; the SELECTED one additionally
 * publishes {@link MAP_PIN_SELECTED_ATTR}.
 *
 * ⚠ These words are SPELLED AGAIN in `web/e2e/helpers/map-popup.ts`, and the
 * comment there says why: the Playwright runner cannot import app `.ts` (the
 * same constraint `helpers/owner-listings.ts` documents for `OWNER_ACTION_ATTR`).
 * The producer's half is what makes them a contract rather than a guess.
 */
export const MAP_PIN_TESTID = 'map-pin';

/** `"true"` on the selected pill — the popover's reference element. */
export const MAP_PIN_SELECTED_ATTR = 'data-selected';

/** The selection props a price pill carries. @see MAP_PIN_SELECTED_ATTR */
export type MapPinSelection = { readonly [K in typeof MAP_PIN_SELECTED_ATTR]: 'true' | 'false' };

/**
 * The pill's selection state, as props for the button that renders it.
 *
 * ⛔ THE ONLY WAY THE ATTRIBUTE IS EVER SPELLED — the same discipline
 * `ownerActionIdentity` applies to `data-action`, and for the same reason: a
 * test that retypes the word pins nothing once the word moves.
 *
 * Every pill carries it, selected or not. An absence claim over a MISSING
 * attribute cannot tell "nothing is selected" from "this pill is not the
 * selected one", and the spec needs both.
 */
export function mapPinSelection(isSelected: boolean): MapPinSelection {
  return { [MAP_PIN_SELECTED_ATTR]: isSelected ? 'true' : 'false' };
}

/** The preview card the popover positions. */
export const MAP_POPUP_TESTID = 'map-popup';

/**
 * The MOBILE preview card — the same listing, over the map's bottom edge,
 * where there is no popover because there is no room for one.
 *
 * It publishes an identity of its own because it is the one element that says
 * whether the results drawer is legitimately off screen. The drawer's openness
 * is DERIVED from exactly this card's presence (`resultsSheetIsOpen`), so a
 * browser test of the stranding asks two questions about the same moment —
 * "is the preview up" and "is the drawer down" — and needs to be able to name
 * both. Mirrored in `web/e2e/helpers/map-popup.ts`.
 */
export const MAP_PREVIEW_TESTID = 'map-preview-card';

/**
 * The map pane — the element `flip`/`shift` are bounded by.
 *
 * It publishes an identity because the spec's every geometric claim is
 * "…inside THIS box", and the box in question is the one the middleware was
 * given. A test that measured some other wrapper could not fail on the
 * boundary being wrong, which is the whole defect.
 */
export const MAP_PANE_TESTID = 'map-pane';

// ---------------------------------------------------------------------------
// The rendered position — a type this module OWNS
// ---------------------------------------------------------------------------

/**
 * Where the card sits, as a style object.
 *
 * ⛔ THIS TYPE IS DECLARED HERE ON PURPOSE, and the reason is a measured CI
 * failure rather than taste. The React binding `@floating-ui/react-dom` returns
 * `floatingStyles: React.CSSProperties` — a REACT-typed property of a
 * third-party declaration file. In the hermetic sandbox that
 * `//web/apps/public:eslint` runs in, that property resolved to an ERROR type
 * (plain `tsc` over the very same tree resolved it fine — `skipLibCheck` hides
 * the declaration diagnostic while the error type survives), so
 * `no-unsafe-assignment` refused both the destructuring and the `style={…}`
 * that consumed it. The PR description carries the measurement.
 *
 * The remedy is not a disable comment. `@floating-ui/dom` — the engine
 * underneath that binding — has ZERO React references in its declarations, and
 * the plumbing the binding adds is one `useEffect` plus an `autoUpdate`
 * subscription, which `ListingsMap.tsx` now owns outright. So the only type
 * that crosses from the library into this app's JSX is this one, and this
 * module declares it.
 *
 * ★ It also buys back the one thing the binding gave for free that this code
 * would otherwise have lost: {@link MAP_POPOVER_UNPLACED} means the card is
 * never painted at the pane's origin for the frame before the first placement
 * lands (`useFloating`'s `isPositioned`).
 */
export interface MapPopoverStyle {
  readonly position: 'absolute';
  readonly left: number;
  readonly top: number;
  /** `hidden` until the first placement lands; `visible` from then on. */
  readonly visibility: 'hidden' | 'visible';
}

/** Before the first placement: in the layout, and not yet painted anywhere. */
export const MAP_POPOVER_UNPLACED: MapPopoverStyle = {
  position: 'absolute',
  left: 0,
  top: 0,
  visibility: 'hidden',
};

/** The placement Floating UI computed, as the style the card renders with. */
export function mapPopoverStyle(x: number, y: number): MapPopoverStyle {
  return { position: 'absolute', left: x, top: y, visibility: 'visible' };
}
